/* eslint-disable object-curly-newline */
import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import Pill from 'components/Pill';
import BeautyPreferencesTooltip from 'components/ProductPage/RatingsAndReviews/ReviewsFilters/BeautyPreferencesTooltip';
import Actions from 'Actions';
import localeUtils from 'utils/LanguageLocale';
import BPUtils from 'utils/BeautyPreferences';
import { customerPreferenceSelector } from 'selectors/user/customerPreferenceSelector';
import BeautyPreferencesFilterEN from 'components/ProductPage/RatingsAndReviews/ReviewsFilters/BeautyPreferencesFilter/locales/BeautyPreferencesFilter_en_US';
import BeautyPreferencesFilterFR from 'components/ProductPage/RatingsAndReviews/ReviewsFilters/BeautyPreferencesFilter/locales/BeautyPreferencesFilter_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(BeautyPreferencesFilterEN, BeautyPreferencesFilterFR);

const categoriesIdsMap = {
    cat140006: 'makeup',
    cat150006: 'skinCare',
    cat130038: 'hair',
    cat160006: 'fragrance'
};

const bpFiltersCategoryMap = {
    cat140006: ['skinType', 'skinConcerns', 'skinTone', 'eyeColor', 'ageRange'], //makeup
    cat150006: ['skinType', 'skinConcerns', 'skinTone', 'ageRange'], //scincare
    cat130038: ['hairColor', 'hairType', 'hairConcerns', 'hairTexture'], //hair
    cat160006: ['fragranceFamily'] //fragrance
};

function BeautyPreferencesFilter({ filterIsSelected = {}, applyFilters, categoryId, reviewFilters, fireAnalytics }) {
    const dispatch = useDispatch();
    const customerPreference = useSelector(customerPreferenceSelector);

    const { filterOptions, isNoOptions } = useMemo(() => {
        const categoryName = categoriesIdsMap[categoryId];
        const refinementsByCategory = BPUtils.getMasterListRefinementsByCategory(categoryId);
        const categoryFilters = refinementsByCategory.filter(
            item => bpFiltersCategoryMap[categoryId].includes(item.key) && reviewFilters.find(filter => filter.id === item.key)
        );

        const options = {};

        categoryFilters.forEach(categoryFilter => {
            //remove items that in master list have filterable: false
            categoryFilter.items = categoryFilter.items.filter(item => item.filterable);

            //when there is no filterable items in master list, don't show this filter
            if (categoryFilter.items.length === 0) {
                return;
            }

            //get user selected BP for every category
            const userCategory = customerPreference[categoryName];
            const userCategoryFilter = userCategory?.[categoryFilter.key];

            if (!userCategory || !userCategoryFilter) {
                return;
            }

            const filteredUserCategoryFilter = userCategoryFilter.filter(
                item => !item.includes('notSure') && !item.includes('noPreferenceFp') //remove options that shouldn't be shown
            );

            //get filter values for every user BP filter from master list
            if (Array.isArray(filteredUserCategoryFilter)) {
                options[categoryFilter.key] = filteredUserCategoryFilter.map(item => {
                    return categoryFilter.items.find(filterOption => filterOption.key === item)?.value || '';
                });
            } else {
                options[categoryFilter.key] = [categoryFilter.items.find(item => item.key === filteredUserCategoryFilter)?.value || ''];
            }
        });

        return {
            filterOptions: options,
            isNoOptions: Object.values(options).flat().length === 0
        };
    }, [categoryId, reviewFilters, customerPreference]);

    const handleFilterClick = () => {
        if (isNoOptions) {
            dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title: getText('modalTitle'),
                    message: getText('modalBody'),
                    buttonText: getText('buttonText')
                })
            );
            fireAnalytics();
        } else {
            applyFilters(filterOptions);
        }
    };

    return (
        <Pill
            data-at={Sephora.debug.dataAt('filter_pill')}
            isActive={filterIsSelected}
            fontSize='sm'
            onClick={handleFilterClick}
        >
            {getText('beautyPreferences')}
            <BeautyPreferencesTooltip isActive={filterIsSelected} />
        </Pill>
    );
}

export default wrapFunctionalComponent(BeautyPreferencesFilter, 'BeautyPreferencesFilter');
